import { useCallback, useEffect, useRef, useState } from "react";
import type {
  SocialCommentHandlingStatusFilter,
  SocialCommentListItemDto,
  SocialCommentPlatformFilter,
} from "@multi-chat/shared";
import {
  listSocialComments,
  SOCIAL_COMMENT_PAGE_SIZE,
} from "../../../api/social-comments-api.js";
import { useInboxRealtime } from "../InboxRealtimeProvider.js";

export type SocialCommentsListFilters = {
  platform?: SocialCommentPlatformFilter;
  handlingStatus: SocialCommentHandlingStatusFilter;
  postId?: string;
  search?: string;
  sort: "activity_desc" | "activity_asc";
};

export type SocialCommentsListView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "empty" }
  | { kind: "searchEmpty" }
  | { kind: "ready"; items: SocialCommentListItemDto[] };

export function useSocialCommentsList(
  organizationId: number,
  filters: SocialCommentsListFilters,
  unreachableMessage: string,
  enabled = true,
): {
  listView: SocialCommentsListView;
  hasMore: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  loadMore: () => boolean;
  refresh: () => void;
  patchItem: (commentId: string, patch: Partial<SocialCommentListItemDto>) => void;
} {
  const { subscribeSocialCommentChanged, subscribeReconnect } = useInboxRealtime();
  const [items, setItems] = useState<SocialCommentListItemDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const orgRef = useRef(organizationId);
  const enabledRef = useRef(enabled);
  const filtersKey = JSON.stringify(filters);

  const fetchPage = useCallback(
    async (mode: "replace" | "append", cursor?: string) => {
      if (!enabledRef.current) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const orgId = organizationId;
      if (mode === "replace") {
        setRefreshing(items.length > 0);
        if (items.length === 0) setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const response = await listSocialComments(orgId, {
          platform: filters.platform,
          handlingStatus: filters.handlingStatus,
          postId: filters.postId,
          search: filters.search,
          sort: filters.sort,
          cursor,
          limit: SOCIAL_COMMENT_PAGE_SIZE,
          signal: controller.signal,
        });
        if (controller.signal.aborted || orgRef.current !== orgId) return;
        setItems((current) =>
          mode === "append" ? [...current, ...response.items] : response.items,
        );
        setNextCursor(response.nextCursor);
        setError(null);
      } catch {
        if (controller.signal.aborted || orgRef.current !== orgId) return;
        if (mode === "replace" && items.length === 0) {
          setError(unreachableMessage);
        }
      } finally {
        if (controller.signal.aborted || orgRef.current !== orgId) return;
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    },
    [filters, items.length, organizationId, unreachableMessage],
  );

  const refresh = useCallback(() => {
    void fetchPage("replace");
  }, [fetchPage]);

  useEffect(() => {
    enabledRef.current = enabled;
    orgRef.current = organizationId;
    if (!enabled) {
      abortRef.current?.abort();
      setLoading(false);
      return;
    }
    setItems([]);
    setNextCursor(null);
    setLoading(true);
    setError(null);
    void fetchPage("replace");
    return () => {
      abortRef.current?.abort();
    };
    // filtersKey captures filter identity without depending on fetchPage's item length.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when org/filters/enabled change
  }, [organizationId, filtersKey, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const unsubEvent = subscribeSocialCommentChanged(() => {
      void fetchPage("replace");
    });
    const unsubReconnect = subscribeReconnect(() => {
      void fetchPage("replace");
    });
    return () => {
      unsubEvent();
      unsubReconnect();
    };
  }, [enabled, fetchPage, subscribeReconnect, subscribeSocialCommentChanged]);

  const loadMore = useCallback((): boolean => {
    if (!nextCursor || loadingMore || loading) return false;
    void fetchPage("append", nextCursor);
    return true;
  }, [fetchPage, loading, loadingMore, nextCursor]);

  const patchItem = useCallback((commentId: string, patch: Partial<SocialCommentListItemDto>) => {
    setItems((current) =>
      current.map((item) => (item.id === commentId ? { ...item, ...patch } : item)),
    );
  }, []);

  let listView: SocialCommentsListView;
  if (loading && items.length === 0) {
    listView = { kind: "loading" };
  } else if (error && items.length === 0) {
    listView = { kind: "error", message: error };
  } else if (items.length === 0) {
    listView =
      filters.search != null && filters.search.trim() !== ""
        ? { kind: "searchEmpty" }
        : { kind: "empty" };
  } else {
    listView = { kind: "ready", items };
  }

  return {
    listView,
    hasMore: nextCursor != null,
    loadingMore,
    refreshing,
    loadMore,
    refresh,
    patchItem,
  };
}
